import React from "react";
import Modal from "react-bootstrap/Modal";
import {
  Room,
  RoomDay,
  Task,
  displayTime,
  declineTitle,
  localInput,
  whiteBadgeFill,
} from "./api";

export interface ActionTarget {
  room: Room;
  day: RoomDay;
  task: Task;
  type: string;
}

const copy: Record<
  string,
  { title: string; commit: string; note?: string; warn?: boolean }
> = {
  complete: { title: "Mark clean as done", commit: "Mark done" },
  decline: {
    title: "Guest declined the clean",
    commit: "Record decline",
    note: "A declined clean is carried to tomorrow's sheet.",
    warn: true,
  },
  reopen: {
    title: "Reopen this clean",
    commit: "Reopen",
    note: "Puts the clean back on today's list. The earlier record stays in the history.",
  },
};

function stayLabel(day: RoomDay): string {
  return day.stayDay != null ? `Stay day ${day.stayDay}` : day.occupancy;
}

export function ActionModal({
  target,
  timezone,
  initials,
  occurredAt,
  reason,
  error,
  busy,
  pending,
  onInitials,
  onOccurredAt,
  onReason,
  onSubmit,
  onClose,
}: {
  target: ActionTarget;
  timezone: string;
  initials: string;
  occurredAt: string;
  reason: string;
  error: string;
  busy: boolean;
  pending: boolean;
  onInitials: (v: string) => void;
  onOccurredAt: (v: string) => void;
  onReason: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
}) {
  const { room, day, task, type } = target;
  const text = copy[type] ?? { title: type, commit: "Record action" };
  const reasonRequired = type === "decline" || type === "reopen";
  const locked = pending || busy;
  const window =
    task.windowStart || task.windowEnd
      ? `Work ${displayTime(task.windowStart, timezone)}${task.windowEnd ? ` to ${displayTime(task.windowEnd, timezone)}` : ""}`
      : "";
  const previous =
    type === "reopen" && task.lastActionStaffInitials
      ? `Marked done by ${task.lastActionStaffInitials}`
      : "";
  const context = [stayLabel(day), window, previous].filter(Boolean);
  return (
    <Modal
      show
      onHide={() => !busy && onClose()}
      aria-label="Record housekeeping action"
      backdrop="static"
      contentClassName="hkm"
    >
      <form onSubmit={onSubmit}>
        <div className="hkm__head">
          <div>
            <h3 className="hkm__title">{type === "decline" ? declineTitle(day) : text.title}</h3>
            <p className="hkm__context">
              <span className="hkm__room">Room {room.roomLabel}</span>
              <span
                className="hk-clean-chip"
                style={{
                  backgroundColor: whiteBadgeFill(task.color),
                  color: "#fff",
                }}
              >
                {task.code}
              </span>
              {context.map((c) => (
                <React.Fragment key={c}>
                  <span className="hkm__dot" aria-hidden="true">
                    ·
                  </span>
                  <span>{c}</span>
                </React.Fragment>
              ))}
            </p>
          </div>
          <button
            type="button"
            className="hkm__close"
            aria-label="Close"
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="hkm__body">
          {text.note && (
            <p className={`hkm__note${text.warn ? " hkm__note--warn" : ""}`}>
              {text.warn && <b>Stays outstanding. </b>}
              {text.note}
            </p>
          )}
          <div className="hkm__row">
            <label className="hkm__field">
              <span className="hkm__label">Staff initials</span>
              <input
                className="hkm__input hkm__input--initials"
                maxLength={20}
                placeholder="e.g. NP"
                value={initials}
                disabled={locked}
                onChange={(e) => onInitials(e.target.value)}
              />
              <span className="hkm__help">From the paper sheet.</span>
            </label>
            <div className="hkm__field">
              <label className="hkm__label" htmlFor="hkm-occurred-at">
                Occurred at
              </label>
              <div className="hkm__datetime">
                <input
                  id="hkm-occurred-at"
                  type="datetime-local"
                  className="hkm__input"
                  required
                  value={occurredAt}
                  disabled={locked}
                  onChange={(e) => onOccurredAt(e.target.value)}
                />
                <button
                  type="button"
                  className="hkm__now"
                  disabled={locked}
                  onClick={() => onOccurredAt(localInput(new Date(), timezone))}
                >
                  Now
                </button>
              </div>
            </div>
          </div>
          <label className="hkm__field">
            <span className="hkm__label">
              {reasonRequired ? "Reason" : "Notes"}
              {reasonRequired ? (
                <span className="hkm__req">required</span>
              ) : (
                <small>optional</small>
              )}
            </span>
            <textarea
              className="hkm__textarea"
              maxLength={1000}
              required={reasonRequired}
              value={reason}
              disabled={locked}
              onChange={(e) => onReason(e.target.value)}
            />
          </label>
          {error && (
            <p role="alert" className="hkm__error">
              <b>Not saved.</b> {error} Retry sends the same action again; close
              and reopen this task to see the latest state.
            </p>
          )}
        </div>
        <div className="hkm__foot">
          <span className="hkm__by">Your account signs this action.</span>
          <span className="hkm__actions">
            <button
              type="button"
              className="btn btn-outline-contrast"
              disabled={busy}
              onClick={onClose}
            >
              {pending ? "Close" : "Cancel"}
            </button>
            <button
              className={`btn ${pending && !busy ? "btn-warning" : "btn-primary"}`}
              disabled={busy}
            >
              {busy ? "Saving..." : pending ? "Retry same action" : text.commit}
            </button>
          </span>
        </div>
      </form>
    </Modal>
  );
}
